import { InboxOutlined } from '@ant-design/icons';
import { useCustomMutation, useTranslate } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { Alert, Button, Card, List, Progress, Space, Typography, Upload } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import {
  IMPORT_MAX_BYTES,
  IMPORT_REFUSAL_CODES,
  IMPORT_RESULT_FIELDS,
  addToUpload,
  estimateSeconds,
  type ImportResult,
} from './importModel';
import { prepareUpload } from './importUpload';
import { RIGHTS_REFUSAL_CODE, RightsConfirmation } from './RightsConfirmation';

/**
 * E34 · Practice — Worksheets — Builder — Import from a file (section) (block 5b; cc-homework, chapter
 * "Worksheets — Templates & library (portal, E34)", screen e34e0009-0001-4000-8000-000000000021). Import a BLANK
 * form (Q-e34-15 A): a PDF, a Word file, or photos / scans as the pages of one document. The draft opens in the
 * editor; nothing is saved until Save. Behavior (the board's):
 *   · gesture: Import → ImportWorksheetTemplate (one request; the file lives in its memory only — Q-e34-17 A);
 *   · help: "Blank forms only — no client information" above the drop zone (the file reaches Google before any
 *     check can see what is on it);
 *   · format: photos shrunk in the browser to about 2,000 px; several photos are ONE document, in the order
 *     chosen; up to 20 MB in all;
 *   · loading-state: a progress bar estimated at about 10 s a page;
 *   · error-display: the recorded refusal the request returns — TOO_LARGE · UNREADABLE · FILLED · BUSY — under
 *     the drop zone ("busy, try again in a minute" for BUSY);
 *   · Q-ugc-1 A: "I have the right to use this content" is ticked before Import (Import stays disabled until it is);
 *     the tick travels on the request and carries to the imported draft's Save — it is asked once.
 */
type Props = {
  /** `rightsConfirmed` — the tick given here, carried to the draft's Save (Q-ugc-1 A). */
  onResult: (result: ImportResult, rightsConfirmed: boolean) => void;
  onClose: () => void;
};

export const WorksheetImportSection = ({ onResult, onClose }: Props) => {
  const t = useTranslate();
  const { mutateAsync } = useCustomMutation<ImportResult>();
  const [files, setFiles] = useState<File[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rights, setRights] = useState(false);
  const [seconds, setSeconds] = useState(10);
  const [percent, setPercent] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearInterval(timer.current);
  }, []);

  const total = files.reduce((n, f) => n + f.size, 0);
  const overLimit = total > IMPORT_MAX_BYTES;

  const add = (picked: File[]) => {
    setRefusal(null);
    const next = addToUpload(files, picked);
    setFiles(next.files);
    setProblem(next.problem ? t(next.problem) : null);
  };

  const start = async () => {
    if (!files.length || busy || !rights) return;
    setRefusal(null);
    setProblem(null);
    setBusy(true);
    setPercent(0);
    try {
      const upload = await prepareUpload(files);
      const estimate = estimateSeconds(upload.pages);
      setSeconds(estimate);
      const began = Date.now();
      timer.current = setInterval(() => {
        // an estimate, never a promise: the bar slows near its end and waits there for the answer
        const done = (Date.now() - began) / 1000 / estimate;
        setPercent(Math.min(95, Math.round(100 * (1 - Math.exp(-2 * done)))));
      }, 500);
      const res = await mutateAsync({
        url: '',
        method: 'post',
        values: {},
        errorNotification: false,
        successNotification: false,
        meta: {
          operation: 'importWorksheetTemplate',
          fields: IMPORT_RESULT_FIELDS,
          variables: {
            input: {
              name: 'input',
              type: 'ImportWorksheetTemplateInput!',
              value: { files: upload.files, rightsConfirmed: true },
            },
          },
        },
      });
      const result = res?.data;
      if (!result?.importId) {
        setRefusal(t('worksheets.import.failed'));
        return;
      }
      if (result.code) {
        setRefusal(
          (IMPORT_REFUSAL_CODES as readonly string[]).includes(result.code)
            ? t(`worksheets.import.errors.${result.code}`)
            : t('worksheets.import.failed'),
        );
        return;
      }
      setPercent(100);
      onResult(result, true);
    } catch (error) {
      const info = graphqlErrorInfo(error);
      if (info.code === RIGHTS_REFUSAL_CODE) setRefusal(t('worksheets.rights.errors.notConfirmed'));
      else if (info.code && info.message) setRefusal(info.message);
      else {
        Sentry.captureException(error);
        setRefusal(t('worksheets.import.failed'));
      }
    } finally {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
      setBusy(false);
    }
  };

  return (
    <Card
      title={t('worksheets.import.title')}
      data-testid="worksheet-import"
      extra={
        <Button onClick={onClose} disabled={busy} data-testid="worksheet-import-close">
          {t('worksheets.import.cancel')}
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text>{t('worksheets.import.intro')}</Typography.Text>
        <Alert
          type="warning"
          showIcon
          message={t('worksheets.import.blankOnly')}
          data-testid="worksheet-import-blank-only"
        />
        <Upload.Dragger
          multiple
          disabled={busy}
          showUploadList={false}
          accept=".pdf,.docx,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
          beforeUpload={(file, list) => {
            // antd calls this once per file with the whole batch — take the batch on its first file
            if (file === list[0]) add(list as unknown as File[]);
            return Upload.LIST_IGNORE;
          }}
          data-testid="worksheet-import-drop"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t('worksheets.import.drop')}</p>
          <p className="ant-upload-hint">{t('worksheets.import.dropHint')}</p>
        </Upload.Dragger>
        <Typography.Text type="secondary" data-testid="worksheet-import-formats">
          {t('worksheets.import.formats')}
        </Typography.Text>
        {files.length ? (
          <List
            size="small"
            bordered
            dataSource={files}
            data-testid="worksheet-import-files"
            renderItem={(f, i) => (
              <List.Item
                data-testid={`worksheet-import-file-${i}`}
                actions={[
                  <Button
                    key="remove"
                    type="link"
                    size="small"
                    disabled={busy}
                    onClick={() => {
                      setFiles(files.filter((_, j) => j !== i));
                      setRefusal(null);
                      setProblem(null);
                    }}
                    data-testid={`worksheet-import-remove-${i}`}
                  >
                    {t('worksheets.import.remove')}
                  </Button>,
                ]}
              >
                {f.name} · {t('worksheets.import.fileSize', { size: (f.size / 1024 / 1024).toFixed(f.size < 1024 * 1024 ? 2 : 1) })}
              </List.Item>
            )}
          />
        ) : null}
        {overLimit ? (
          <Typography.Text type="warning" data-testid="worksheet-import-over-limit">
            {t('worksheets.import.overLimit', { size: (total / 1024 / 1024).toFixed(1) })}
          </Typography.Text>
        ) : null}
        {problem ? (
          <Alert type="error" showIcon message={problem} data-testid="worksheet-import-problem" />
        ) : null}
        {busy ? (
          <div data-testid="worksheet-import-progress">
            <Progress percent={percent} status="active" />
            <Typography.Text type="secondary">
              {t('worksheets.import.progress', { seconds })}
            </Typography.Text>
          </div>
        ) : null}
        <RightsConfirmation
          checked={rights}
          onChange={setRights}
          disabled={busy}
          testId="worksheet-import-rights"
        />
        {/* Q-mt-2 A: an error from pressing Import sits directly above the button (the rights box goes ABOVE it);
            a problem with the file itself stays at the drop zone (above) and disables Import while it holds */}
        {refusal ? (
          <Alert type="error" showIcon message={refusal} data-testid="worksheet-import-error" />
        ) : null}
        <Button
          type="primary"
          onClick={start}
          loading={busy}
          disabled={!files.length || !!problem || !rights}
          data-testid="worksheet-import-start"
        >
          {t('worksheets.import.start')}
        </Button>
      </Space>
    </Card>
  );
};
